import React, {useEffect, useMemo, useState} from 'react'

type RevisionVendorOption = {
  quotationId: number
  vendorName: string
  status: string
  submittedAt?: string | null
}

type Props = {
  open: boolean
  vendors: RevisionVendorOption[]
  submitting?: boolean
  maxRemarkLength?: number
  onClose: () => void
  onSubmit: (payload: {quotationIds: number[]; remarks: string}) => Promise<void> | void
}

const QuotationRevisionRequestModal: React.FC<Props> = ({
  open,
  vendors,
  submitting = false,
  maxRemarkLength = 10000,
  onClose,
  onSubmit,
}) => {
  const [selectedQuotationIds, setSelectedQuotationIds] = useState<number[]>([])
  const [remark, setRemark] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) {
      setSelectedQuotationIds([])
      setRemark('')
      setError('')
    }
  }, [open])

  const allQuotationIds = useMemo(() => vendors.map((vendor) => vendor.quotationId), [vendors])
  const allSelected = allQuotationIds.length > 0 && allQuotationIds.every((id) => selectedQuotationIds.includes(id))

  if (!open) return null

  const toggleQuotation = (quotationId: number) => {
    setSelectedQuotationIds((current) =>
      current.includes(quotationId) ? current.filter((id) => id !== quotationId) : [...current, quotationId]
    )
    if (error) setError('')
  }

  const handleSelectAll = () => {
    setSelectedQuotationIds(allSelected ? [] : allQuotationIds)
    if (error) setError('')
  }

  const handleSubmit = async () => {
    const trimmedRemark = remark.trim()
    if (selectedQuotationIds.length === 0) {
      setError('Select at least one vendor.')
      return
    }
    if (!trimmedRemark) {
      setError('Revision remarks are required.')
      return
    }
    if (trimmedRemark.length > maxRemarkLength) {
      setError(`Revision remarks must not exceed ${maxRemarkLength} characters.`)
      return
    }
    setError('')
    await onSubmit({quotationIds: selectedQuotationIds, remarks: trimmedRemark})
  }

  return (
    <div
      className='modal fade show d-flex align-items-center justify-content-center'
      style={{backgroundColor: 'rgba(0,0,0,0.45)', position: 'fixed', inset: 0, zIndex: 1060}}
    >
      <div className='modal-dialog modal-lg modal-dialog-centered'>
        <div className='modal-content bg-white'>
          <div className='modal-header'>
            <div>
              <h5 className='modal-title mb-1'>Request Revision</h5>
              <div className='text-muted small'>Select one or more vendors with submitted quotations.</div>
            </div>
            <button type='button' className='btn-close' disabled={submitting} onClick={onClose} />
          </div>
          <div className='modal-body'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <label className='form-label fw-semibold mb-0'>Eligible Vendors</label>
              <button type='button' className='btn btn-sm btn-light-primary' disabled={vendors.length === 0} onClick={handleSelectAll}>
                {allSelected ? 'Clear All' : 'Select All'}
              </button>
            </div>
            <div className='border rounded-3 p-3 mb-4' style={{maxHeight: 260, overflowY: 'auto'}}>
              {vendors.length === 0 ? (
                <div className='text-muted'>No submitted vendors are eligible for revision.</div>
              ) : (
                vendors.map((vendor) => (
                  <label
                    key={vendor.quotationId}
                    className='d-flex align-items-start gap-3 border rounded-3 p-3 mb-2'
                    style={{cursor: 'pointer'}}
                  >
                    <input
                      type='checkbox'
                      className='form-check-input mt-1'
                      checked={selectedQuotationIds.includes(vendor.quotationId)}
                      onChange={() => toggleQuotation(vendor.quotationId)}
                    />
                    <div className='flex-grow-1'>
                      <div className='fw-semibold text-dark'>{vendor.vendorName}</div>
                      <div className='text-muted small'>
                        Quotation #{vendor.quotationId} · {String(vendor.status || 'SUBMITTED').replace(/_/g, ' ')}
                      </div>
                      {vendor.submittedAt ? (
                        <div className='text-muted small'>Submitted: {String(vendor.submittedAt).slice(0, 10)}</div>
                      ) : null}
                    </div>
                  </label>
                ))
              )}
            </div>

            <label className='form-label fw-semibold mb-2'>
              Revision Remarks <span className='text-danger'>*</span>
            </label>
            <textarea
              className='form-control'
              rows={5}
              value={remark}
              maxLength={maxRemarkLength}
              placeholder='Explain what the vendor must revise in the quotation.'
              onChange={(event) => {
                setRemark(event.target.value)
                if (error) setError('')
              }}
            />
            <div className='text-muted small mt-2'>
              {remark.trim().length}/{maxRemarkLength}
            </div>
            {error ? <div className='text-danger small mt-2'>{error}</div> : null}
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light' disabled={submitting} onClick={onClose}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-primary'
              disabled={submitting || vendors.length === 0 || selectedQuotationIds.length === 0 || !remark.trim() || remark.trim().length > maxRemarkLength}
              onClick={() => void handleSubmit()}
            >
              {submitting ? 'Sending...' : 'Send Revision Request'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export type {RevisionVendorOption}
export default QuotationRevisionRequestModal
